// ─────────────────────────────────────────────
//  Break engine — pure state machine (no Firebase)
//  Each provider has, per day, four independent channels. Meal channels
//  (breakfast/lunch/dinner) lock when done; bathroom is repeatable.
//  This module owns the rules; Firestore writes are layered on top elsewhere.
// ─────────────────────────────────────────────

export const BREAK_CHANNELS = ["breakfast", "lunch", "dinner", "bathroom"];
export const MEAL_CHANNELS = ["breakfast", "lunch", "dinner"];

export const isMeal = (channel) => MEAL_CHANNELS.includes(channel);
export const isBathroom = (channel) => channel === "bathroom";

// Map any stored break-type value ("Bathroom", "LUNCH ", legacy caps from the
// old request modal) onto its canonical channel; null when it isn't one
// (e.g. legacy "Other"). Callers decide their own fallback. This is what keeps
// the type REQUESTED identical to the type shown on the board card.
export function normalizeBreakChannel(value) {
  const v = String(value || "").trim().toLowerCase();
  return BREAK_CHANNELS.includes(v) ? v : null;
}

// Channel states and their display colors (per the mockup).
export const BREAK_STATES = {
  none:        { label: "—",          color: "#555e7a", flashing: false },
  requested:   { label: "Requested",  color: "#ef4444", flashing: true  }, // bathroom only
  // ⚠️ NAMED, NOT COVERED. Somebody has been ASKED to cover this channel and is
  // on their way; the room still has nobody in it and the provider still has
  // their hands on the patient. Anything that reads this as "covered" stops the
  // board asking for help for a room that still needs it — which is the exact
  // lie this state was added to remove. `assignCover` writes it; only the named
  // coverer's own arrival tap moves it on.
  assigned:    { label: "Coming",     color: "#1e40af", flashing: false },
  in_progress: { label: "On break",   color: "#f59e0b", flashing: false }, // reliever covering
  done:        { label: "Done",       color: "#10b981", flashing: false },
};

export const stateMeta = (state) => BREAK_STATES[state] || BREAK_STATES.none;

// A fresh day's break record: every channel starts at "none".
export function initialBreaks() {
  return BREAK_CHANNELS.reduce((acc, ch) => {
    acc[ch] = "none";
    return acc;
  }, {});
}

export function getChannelState(breaks, channel) {
  return (breaks && breaks[channel]) || "none";
}

// ── Transition guards ─────────────────────────
// Each returns the next state, or throws on an illegal transition so callers
// can surface a clear error rather than writing nonsense to the database.

function assertChannel(channel) {
  if (!BREAK_CHANNELS.includes(channel)) {
    throw new Error(`Unknown break channel: ${channel}`);
  }
}

// Provider takes their own meal break (no reliever). Meals only.
export function selfBreak(breaks, channel) {
  assertChannel(channel);
  if (!isMeal(channel)) throw new Error("Self break is only for meals.");
  const state = getChannelState(breaks, channel);
  if (state === "done") {
    throw new Error(`${channel} is already done.`);
  }
  // Marking your own meal taken while a colleague is walking over to cover it
  // strands the coverer in a room that no longer needs them.
  if (state === "assigned") {
    throw new Error(`${channel} already has a cover assigned — cancel it first.`);
  }
  return { ...breaks, [channel]: "done" };
}

// Provider asks for a bathroom break (flashes red until someone covers).
export function requestBathroom(breaks) {
  const state = getChannelState(breaks, "bathroom");
  if (state === "in_progress") throw new Error("A bathroom break is already in progress.");
  if (state === "assigned") throw new Error("A cover assigned for the bathroom is already on their way.");
  return { ...breaks, bathroom: "requested" };
}

// ── The two halves of giving a break ──────────
//
// These were ONE function (`giveBreak`), and that was a lie on a clinical board:
// it wrote `in_progress` on the AIC's click, so the board said "on break, X
// covering" before anyone had walked anywhere, before the person on break knew,
// and before any handoff had been given. Splitting them is what makes the
// board's claim of coverage true.

// Somebody has been ASKED to cover this channel. The room is NOT yet covered.
export function assignCover(breaks, channel) {
  assertChannel(channel);
  const state = getChannelState(breaks, channel);
  // A second assign would silently overwrite the scalar relieverId/relieverName
  // and the first person would never be told they were stood down.
  if (state === "assigned")    throw new Error(`${channel} already has a cover assigned.`);
  if (state === "in_progress") throw new Error(`${channel} break is already being covered.`);
  if (isMeal(channel) && state === "done") throw new Error(`${channel} is already done.`);

  // ⚠️ ONE ROOM, ONE LIVE COVER — AND THIS IS A DATA-INTEGRITY RULE, NOT A
  // POLICY PREFERENCE. `relieverId` / `relieverName` are SCALAR fields on the
  // room, so a second live channel has nowhere of its own to record who is
  // covering it: the newer assign overwrites the older one, the first coverer
  // silently loses `iAmCover` over the break they are actually standing in, and
  // `activeChannel` — a `find()` over BREAK_CHANNELS — then resolves "End my
  // break" to whichever channel happens to sort first. Somebody ends a break
  // that is not theirs.
  //
  // The alternative fix is a per-channel cover map, but that is a NEW TOP-LEVEL
  // FIELD and firestore.rules' provider break path is a `hasOnly()` allowlist —
  // it would be permission-denied for every provider, which on a phone looks
  // exactly like "the button did nothing". A rule costs nothing and needs no
  // deploy.
  //
  // It is also what happens clinically: a provider already away on their lunch
  // relief does not simultaneously need somebody covering their bathroom break.
  const busy = BREAK_CHANNELS.find(
    (ch) => ch !== channel && (getChannelState(breaks, ch) === "assigned" || getChannelState(breaks, ch) === "in_progress")
  );
  if (busy) {
    throw new Error(
      getChannelState(breaks, busy) === "in_progress"
        ? `${busy} is already being covered in this room — end that break first.`
        : `${busy} already has a cover on the way to this room.`
    );
  }

  return { ...breaks, [channel]: "assigned" };
}

// The named cover is physically in the room and has taken handoff. THIS is the
// write that makes the room covered, and only the coverer may make it.
export function arriveForCover(breaks, channel) {
  assertChannel(channel);
  const state = getChannelState(breaks, channel);
  // Idempotent rather than an error: a double-tap on a phone must not toast a
  // failure at the one person the whole board is waiting on.
  if (state === "in_progress") return { ...breaks };
  if (state !== "assigned") throw new Error(`Nobody is assigned to cover ${channel}.`);
  return { ...breaks, [channel]: "in_progress" };
}

// The assigned cover never came, was stood down, or the case turned and the
// break has to be called off.
//
// ⚠️ WITHOUT THIS, `completeBreak` IS THE ONLY EXIT FROM `assigned`, and for a
// meal that writes `done` — the board locks lunch green for somebody who never
// ate, and stops asking for them for the rest of the day. `wasRequested` sends
// a provider-initiated bathroom back to `requested` rather than to `none`: the
// need did not go away, only the timing did, and dropping to `none` deletes a
// waiting CRNA from the board entirely.
export function cancelCover(breaks, channel, { wasRequested = false } = {}) {
  assertChannel(channel);
  if (getChannelState(breaks, channel) !== "assigned") {
    throw new Error(`${channel} has no cover to cancel.`);
  }
  const back = wasRequested && isBathroom(channel) ? "requested" : "none";
  return { ...breaks, [channel]: back };
}

// The covered break finishes: meals lock green, bathroom resets to none.
export function completeBreak(breaks, channel) {
  assertChannel(channel);
  // ⚠️ NOT FROM `assigned`. A break nobody ever started cannot be finished —
  // completing it would record a meal as taken by a provider who never left
  // the room. Cancel it instead.
  if (getChannelState(breaks, channel) === "assigned") {
    throw new Error(`${channel} was assigned but never started — cancel it instead.`);
  }
  return { ...breaks, [channel]: isBathroom(channel) ? "none" : "done" };
}

// How long a named cover may be "on their way" before the board says so, in
// minutes, by channel.
//
// ⚠️ ONE NAMED CONSTANT, NOT A LITERAL IN THREE FILES. No walk in an OR suite
// takes more than about five minutes, so ten means the person was pulled into
// something, never saw it, or left their phone in a locker — and by then the
// provider waiting has been TOLD help is coming and has stopped asking, which
// is worse than never having been told. The bathroom channel is a five-minute
// promise, so it runs out sooner.
export const COVER_LATE_MIN = { meal: 10, bathroom: 4 };

export const coverLateAfter = (channel) =>
  isBathroom(channel) ? COVER_LATE_MIN.bathroom : COVER_LATE_MIN.meal;

// ── Derived views for the UI ──────────────────

// Is a reliever currently covering ANY channel for this provider?
//
// ⚠️ `assigned` RETURNS FALSE, AND THAT IS THE POINT OF THIS PREDICATE. It
// answers exactly one question — "is somebody standing in that room" — and it
// is what stops the board offering the room to a second cover, prints
// "X is covering", and marks the room `covered`. An assigned-but-unarrived room
// is none of those things. See `isCoverCommitted` for the OTHER question,
// which reads a person's own record and does include `assigned`.
export function isBeingRelieved(breaks) {
  return BREAK_CHANNELS.some((ch) => getChannelState(breaks, ch) === "in_progress");
}

// Has somebody been asked to cover a channel who has not yet arrived?
export function isCoverPending(breaks) {
  return BREAK_CHANNELS.some((ch) => getChannelState(breaks, ch) === "assigned");
}

// Is this person spoken for — either covering now or on their way?
//
// This is the question to ask of a CANDIDATE's own record when building a
// give-a-break picker: somebody already walking to another room is not free,
// even though nobody is relieving them. Asking `isBeingRelieved` there offers
// the same person to two rooms.
export const isCoverCommitted = (breaks) => isBeingRelieved(breaks) || isCoverPending(breaks);

export function hasPendingBathroom(breaks) {
  return getChannelState(breaks, "bathroom") === "requested";
}

// Channels a provider could still take a meal break in (for menus).
export function availableMealBreaks(breaks) {
  return MEAL_CHANNELS.filter((ch) => getChannelState(breaks, ch) === "none");
}

// Build the 4-cell status row the board/provider card renders.
export function breakStatusRow(breaks) {
  return BREAK_CHANNELS.map((channel) => {
    const state = getChannelState(breaks, channel);
    const meta = stateMeta(state);
    return { channel, state, color: meta.color, flashing: meta.flashing, label: meta.label };
  });
}

// ── Compact board helpers ─────────────────────
// Letter codes for the at-a-glance break board. Bathroom uses the symbol
// requested by the user; components must pair it with aria-label="bathroom"
// since the glyph alone is not accessible text.
export const CHANNEL_LETTERS = {
  breakfast: "B",
  lunch: "L",
  dinner: "D",
  bathroom: "🚻",
};

export const channelLetter = (channel) => CHANNEL_LETTERS[channel] || channel;

// Derived view for the compact break board: one cell per channel with
// letter code + active/done flags. active is true for in_progress OR
// requested (these blink). done is true for state "done".
// Pure function — no transitions, no Firestore imports.
// ⚠️ `assigned` GETS ITS OWN FLAG rather than joining `active`. BreakBoard turns
// `active` straight into the accessible name "on break" — widening it would
// announce a covered room to the one reader who cannot see the colour, about a
// room nobody has entered.
export function compactStatusRow(breaks) {
  return BREAK_CHANNELS.map((channel) => {
    const state = getChannelState(breaks, channel);
    const letter = channelLetter(channel);
    const active = state === "in_progress" || state === "requested";
    const pending = state === "assigned";
    const done = state === "done";
    return { channel, letter, state, active, pending, done };
  });
}

// ── Supervisor-approved break loop (Phase 5.3) ─
// Display-cased request types ("Lunch", "Bathroom", "Other") → engine channel.
// Unknown/Other map to "bathroom": it is the only repeatable channel, so a
// mis-typed request can never permanently lock a meal to "done".
export function channelForType(type) {
  const t = String(type || "").toLowerCase();
  return BREAK_CHANNELS.includes(t) ? t : "bathroom";
}

// Countdown for a running approved break window. Pure: caller supplies `now`.
// Returns null when no window is running; otherwise
// { remainingMin, overdue, label } — label is board-ready copy.
export function breakCountdown({ startedAt, minutes, now = new Date() } = {}) {
  if (!startedAt || !minutes) return null;
  const startMs = typeof startedAt === "number" ? startedAt : Date.parse(startedAt);
  if (!Number.isFinite(startMs)) return null;
  const endMs = startMs + minutes * 60000;
  const remainingMs = endMs - (now instanceof Date ? now.getTime() : now);
  const remainingMin = Math.ceil(remainingMs / 60000);
  if (remainingMs >= 0) {
    return { remainingMin, overdue: false, label: `${remainingMin}m left` };
  }
  return { remainingMin, overdue: true, label: `overdue ${Math.abs(Math.floor(remainingMs / 60000))}m` };
}

// ── When each break happened ──────────────────
//
// `breaks` records STATE per channel and nothing else, so a board can say a
// lunch is done but never when it was taken. `breakTimes` is a sibling map of
// channel -> "HH:MM" recording the moment a channel last changed hands.
//
// A SEPARATE map rather than turning `breaks[channel]` into an object: the
// state machine, the rules allowlist, the compact row and every existing room
// document all assume a plain string there. Widening that shape would be a
// migration across the whole app to add a display detail. Absent times simply
// render without one, so old rooms keep working untouched.
export function stampBreakTime(breakTimes, channel, hhmm) {
  if (!BREAK_CHANNELS.includes(channel)) return breakTimes || {};
  if (typeof hhmm !== "string" || !/^([01]\d|2[0-3]):[0-5]\d$/.test(hhmm)) {
    return breakTimes || {};
  }
  return { ...(breakTimes || {}), [channel]: hhmm };
}

export function breakTimeFor(breakTimes, channel) {
  const t = breakTimes && breakTimes[channel];
  return typeof t === "string" && /^([01]\d|2[0-3]):[0-5]\d$/.test(t) ? t : null;
}

// "HH:MM" for right now, in the device's local time — which is the hospital's
// local time, since the board is used on site.
export function nowHm(date = new Date()) {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

// ── How long somebody has gone without a meal ──
//
// The attending, reviewing the break redesign: "worth more than the whole
// messaging feature — 'in room since 07:00, no lunch, 5h20m', sorted by need.
// The data already exists." It did: the day's rows knew when each person
// started, `breaks` knew which meals were done, and `breakTimes` knew when. No
// surface put the three together, so the one question a break CRNA actually
// walks around asking — who needs a break most — could only be answered by
// reading fourteen rows and doing the arithmetic in your head.
//
// Pure, and deliberately takes MINUTES rather than rows: this module knows
// nothing about assignments, and the caller already resolves which row claims a
// person (`rowClaimsAt`) for four other reasons.

// The clock of the most recent meal this provider has actually TAKEN, or null.
//
// ⚠️ ONLY `done` COUNTS. An assigned or in-progress meal carries a `breakTimes`
// stamp too, and reading the stamp alone would report somebody as fed while
// they are still standing in their room waiting for the cover to walk in — the
// same class of lie the assigned/in_progress split exists to remove.
// The bathroom is never a meal, however many times it has been taken.
export function lastMealAtMin(breaks, breakTimes) {
  let latest = null;
  for (const channel of MEAL_CHANNELS) {
    if (getChannelState(breaks, channel) !== "done") continue;
    const at = breakTimeFor(breakTimes, channel);
    if (!at) continue;
    const [h, m] = at.split(":");
    const mins = Number(h) * 60 + Number(m);
    if (latest == null || mins > latest) latest = mins;
  }
  return latest;
}

// "5h20m" / "45m" / "1h" — how a board says a duration.
function gapLabel(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h${m}m` : `${h}h`;
}

// How long since this person last ate — or since their day began, if they have
// not. Returns null when the board cannot know.
//
// ⚠️ NULL, NOT ZERO, WHEN A FACT IS MISSING. A person whose start time never
// made it onto the row is the one the board knows LEAST about; scoring them 0
// would sort them to the bottom of the "who needs a break" list, which is
// precisely backwards. `byMealNeed` parks them at the end as unknown instead of
// claiming they are fine.
export function mealGap({ onSinceMin, lastMealMin = null, nowMin } = {}) {
  if (!Number.isFinite(nowMin)) return null;
  const sinceMin = Number.isFinite(lastMealMin) ? lastMealMin : onSinceMin;
  if (!Number.isFinite(sinceMin)) return null;
  const minutes = nowMin - sinceMin;
  // A negative gap is a clock problem, not a well-fed clinician.
  if (minutes < 0) return null;
  return {
    minutes,
    sinceMin,
    hadMeal: Number.isFinite(lastMealMin),
    label: gapLabel(minutes),
  };
}

// A meal gap this long is worth the eye going to it first. NOT an alarm and
// never alarm-coloured: nobody is in danger, somebody is hungry, and spending
// the board's red on this is how red stops meaning anything.
export const MEAL_GAP_LONG_MIN = 5 * 60;

// "07:30" from minutes past midnight.
const minutesToHm = (m) =>
  `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

// "in room since 07:00 · no meal break · 5h20m", or "" when the board cannot say.
//
// ⚠️ SILENT WHEN IT DOES NOT KNOW. A row whose start time never made it onto the
// assignment carries no gap, and printing "0m" there would state that the person
// the board knows LEAST about is the freshest in the department.
//
// ⚠️ LIVES HERE BECAUSE TWO SCREENS SAY IT. The web BREAKS card and the phone's
// break board are the same board; `breakBoardRows` already sorts both by meal
// need, and a sentence written twice is how two surfaces start describing one
// fact differently.
export function mealNeedLine(row) {
  if (!Number.isFinite(row?.mealGapMin) || !row?.mealGapLabel) return "";
  const since = Number.isFinite(row.onSinceMin) ? minutesToHm(row.onSinceMin) : null;
  const head = since && !row.hadMeal ? `in room since ${since}` : null;
  const meal = row.hadMeal
    ? `last break ${minutesToHm(row.lastMealMin)}`
    : "no meal break";
  return [head, meal, row.mealGapLabel].filter(Boolean).join(" · ");
}


// Sort comparator: longest gap first, unknown last. Stable for equal gaps, so
// a board does not reshuffle two people every minute for no reason.
export function byMealNeed(a, b) {
  const av = Number.isFinite(a?.mealGapMin) ? a.mealGapMin : -1;
  const bv = Number.isFinite(b?.mealGapMin) ? b.mealGapMin : -1;
  return bv - av;
}
